import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';
import OrderCard from '../components/OrderCard';
import OrderList from '../components/OrderList';
import OrderDetailsModal from '../components/OrderDetailsModal';
import io from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';

const Orders = ({ isDarkMode }) => {
    const { dbUser } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('board'); // 'board' | 'list'
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const audioRef = useRef(null);
    const socketRef = useRef(null);
    const [restaurantId, setRestaurantId] = useState(null);

    // Initial Fetch & Socket Setup
    useEffect(() => {
        // Setup Audio
        audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');

        fetchOrders();

        const restId = dbUser?.restaurant?.id;
        if (restId) {
            setRestaurantId(restId);
            connectSocket(restId);
        }

        return () => {
            if (socketRef.current) {
                console.log('[Socket] Cleaning up connection...');
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [dbUser?.restaurant?.id]); // Only re-run if restaurant ID changes

    const fetchOrders = async () => {
        try {
            const res = await api.get('/restaurant/orders');
            setOrders(res.data.orders);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const connectSocket = (restId) => {
        if (socketRef.current) return;

        // Use environment variable or fallback to production URL
        const socketUrl = process.env.REACT_APP_SOCKET_URL || 'https://dingit-production.up.railway.app';

        console.log(`[Socket] Connecting to ${socketUrl} for Restaurant ${restId}...`);

        const socket = io(socketUrl, {
            transports: ['websocket', 'polling']
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            console.log(`[Socket] Connected! ID: ${socket.id}`);
            setIsConnected(true);
            const roomName = `restaurant_${restId}`;
            console.log(`[Socket] Joining room: ${roomName}`);

            // Join room: restaurant_{id}
            socket.emit('join', { room: roomName });
        });

        socket.on('test', (data) => {
            console.log('[Socket] Received test message:', data);
        });

        socket.on('joined', (room) => {
            console.log('[Socket] Successfully joined room:', room);
        });

        socket.on('disconnect', (reason) => {
            console.log('[Socket] Disconnected. Reason:', reason);
            setIsConnected(false);
        });

        socket.on('connect_error', (error) => {
            console.error('[Socket] Connection error:', error);
            setConnectionError(error.message);
            setIsConnected(false);
        });

        // NODE.JS BACKEND EVENT
        socket.on('order:new', (data) => {
            console.log("Received order:new event:", data);
            playAlert();
            setOrders(prev => [data, ...prev]);
        });

        // Listen for status updates
        socket.on('order:update', (updatedOrder) => {
            console.log("Received order:update:", updatedOrder);
            setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
        });

        // FLASK BACKEND EVENT (Legacy - Removed to prevent duplicate/confusion)
        // socket.on(`restaurant_${restId}`, (msg) => { ... });

        // Listen for raw object if emitted directly? Unlikely but possible.
    };

    const playAlert = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.log('Audio play blocked', e));
        }
    };

    const updateStatus = async (orderId, status, note = '') => {
        try {
            // Optimistic update for UI responsiveness
            setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));

            // Actually call backend
            // If backend is active, this works. If we are simulating, we intercept.
            if (orderId.startsWith('sim-')) {
                // Simulated order update
                return;
            }

            const payload = { status };
            if (note) {
                if (status === 'REJECTED') payload.rejectionReason = note;
                else payload.delayReason = note; // Generalized for now, can be specific if needed
            }

            await api.put(`/orders/${orderId}/status`, payload);
        } catch (error) {
            console.error(error);
            fetchOrders(); // Revert on fail
        }
    };

    const simulateNewOrder = () => {
        const dummyId = `sim-${Date.now()}`;
        const dummyOrder = {
            id: dummyId,
            orderNumber: `ORD-${Math.floor(Math.random() * 1000)}`,
            restaurantId: restaurantId || 'simular-rest-id',
            customerId: 'sim-cust',
            customerName: 'Test Customer',
            customerPhone: '9876543210',
            deliveryAddress: '123 Main St, Springfield',
            paymentStatus: Math.random() > 0.3 ? 'PAID' : 'COD',
            orderType: Math.random() > 0.5 ? 'DELIVERY' : 'DINE_IN',
            status: 'RECEIVED',
            estimatedPrepTime: 25, // 25 mins
            totalAmount: (Math.random() * 500 + 100).toFixed(2),
            createdAt: new Date().toISOString(),
            items: [
                {
                    menuItemName: 'Butter Chicken',
                    quantity: 1,
                    customizations: { 'Spice Level': 'Medium' }
                },
                {
                    menuItemName: 'Naan',
                    quantity: 2,
                    customizations: {}
                }
            ],
            specialInstructions: Math.random() > 0.7 ? 'Leave at door please.' : ''
        };

        playAlert();
        setOrders(prev => [dummyOrder, ...prev]);
    };

    // Columns configuration for Board View
    const columns = [
        { id: 'RECEIVED', title: 'New Orders', color: '#3498db' },
        { id: 'ACCEPTED', title: 'Accepted', color: '#f1c40f' },
        { id: 'PREPARING', title: 'Preparing', color: '#e67e22' },
        { id: 'READY', title: 'Ready', color: '#2ecc71' }
    ];

    // Group orders
    const getOrdersByStatus = (status) => {
        if (!orders) return [];
        if (status === 'RECEIVED') {
            return orders.filter(o => o.status === 'RECEIVED' || o.status === 'PENDING');
        }
        return orders.filter(o => o.status === status);
    };

    const styles = getStyles(isDarkMode);

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: isDarkMode ? '#fff' : '#333' }}>Loading Live Dashboard...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h2 style={styles.title}>Live Order Dashboard</h2>
                    <p style={styles.subtitle}>{orders?.length || 0} active orders • {new Date().toLocaleDateString()}</p>
                </div>
                <div style={styles.controls}>
                    <div style={styles.toggleGroup}>
                        <div style={{ padding: '0 10px', fontSize: '0.8rem', color: isConnected ? '#2ecc71' : '#e74c3c', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isConnected ? '#2ecc71' : '#e74c3c' }}></div>
                            {isConnected ? 'Live' : (connectionError ? `Error: ${connectionError}` : 'Offline')}
                        </div>
                        <button
                            style={{ ...styles.toggleBtn, ...(viewMode === 'board' ? styles.toggleBtnActive : {}) }}
                            onClick={() => setViewMode('board')}
                        >
                            Board
                        </button>
                        <button
                            style={{ ...styles.toggleBtn, ...(viewMode === 'list' ? styles.toggleBtnActive : {}) }}
                            onClick={() => setViewMode('list')}
                        >
                            List
                        </button>
                    </div>

                    <button onClick={simulateNewOrder} style={styles.simBtn}>
                        🔔 Test Alert
                    </button>
                    <button onClick={fetchOrders} style={styles.refreshBtn}>
                        🔄 Refresh
                    </button>
                </div>
            </div>

            {viewMode === 'board' ? (
                <div style={styles.board}>
                    {columns.map(col => (
                        <div key={col.id} style={styles.column}>
                            <div style={styles.columnHeader}>
                                <div style={{ ...styles.statusDot, backgroundColor: col.color }}></div>
                                <span style={styles.colTitle}>{col.title}</span>
                                <span style={styles.countBadge}>{getOrdersByStatus(col.id).length}</span>
                            </div>

                            <div style={styles.columnContent}>
                                {getOrdersByStatus(col.id).length === 0 && (
                                    <div style={styles.emptyState}>
                                        No {col.title.toLowerCase()}
                                    </div>
                                )}
                                {getOrdersByStatus(col.id).map(order => (
                                    <OrderCard
                                        key={order.id}
                                        order={order}
                                        onUpdateStatus={updateStatus}
                                        isDarkMode={isDarkMode}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <OrderList
                    orders={orders}
                    onUpdateStatus={updateStatus}
                    onViewDetails={setSelectedOrder}
                    isDarkMode={isDarkMode}
                />
            )}

            <OrderDetailsModal
                order={selectedOrder}
                isOpen={!!selectedOrder}
                onClose={() => setSelectedOrder(null)}
                onUpdateStatus={updateStatus}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

const getStyles = (isDark) => ({
    container: {
        padding: '24px 32px',
        height: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDark ? '#121212' : '#F9F9FB', // Very subtle grey for light mode
        fontFamily: "'Inter', sans-serif"
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        color: isDark ? '#fff' : '#2D3436'
    },
    title: {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: '700',
        letterSpacing: '-0.5px'
    },
    subtitle: {
        margin: '4px 0 0 0',
        color: isDark ? '#888' : '#636E72',
        fontSize: '0.9rem'
    },
    controls: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    toggleGroup: {
        display: 'flex',
        backgroundColor: isDark ? '#333' : '#e0e0e0',
        borderRadius: '8px',
        padding: '4px',
        marginRight: '12px'
    },
    toggleBtn: {
        padding: '6px 16px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: isDark ? '#aaa' : '#666',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '0.9rem'
    },
    toggleBtnActive: {
        backgroundColor: isDark ? '#555' : '#fff',
        color: isDark ? '#fff' : '#333',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    simBtn: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6C5CE7',
        color: 'white',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 10px rgba(108, 92, 231, 0.3)',
        transition: 'transform 0.1s'
    },
    refreshBtn: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: isDark ? '1px solid #444' : '1px solid #ddd',
        backgroundColor: isDark ? '#2D3436' : '#fff',
        color: isDark ? '#ccc' : '#2D3436',
        fontWeight: '600',
        cursor: 'pointer'
    },
    board: {
        display: 'flex',
        gap: '24px',
        overflowX: 'auto',
        flex: 1,
        paddingBottom: '20px'
    },
    column: {
        flex: '0 0 320px', // Fixed width columns
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    columnHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '0 4px'
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        marginRight: '8px'
    },
    colTitle: {
        fontSize: '1rem',
        fontWeight: '700',
        color: isDark ? '#eee' : '#2D3436',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginRight: '8px'
    },
    countBadge: {
        backgroundColor: isDark ? '#333' : '#dfe6e9',
        color: isDark ? '#ccc' : '#636e72',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: '700'
    },
    columnContent: {
        flex: 1,
        overflowY: 'auto', // Scrollable content within column
        paddingRight: '4px', // Space for scrollbar
        paddingBottom: '20px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
        color: isDark ? '#444' : '#b2bec3',
        border: '2px dashed',
        borderColor: isDark ? '#333' : '#e0e0e0',
        borderRadius: '12px',
        marginTop: '10px',
        fontSize: '0.9rem',
        fontWeight: '500'
    }
});

export default Orders;
